import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { signOut } from '@/lib/auth/session';
import { IconLogIn, IconLogOut } from '@/components/home/icons';

/**
 * Header auth control. Renders an icon-only Sign-in link when anonymous,
 * and an avatar (links to Settings) + icon-only Sign-out when signed in.
 */
export function AuthMenu() {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  if (session.isLoading) {
    return <div aria-hidden className="h-9 w-9" />;
  }

  if (!session.data) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return (
      <Link
        to={`/login?next=${next}`}
        aria-label="Sign in"
        title="Sign in"
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-night-200 transition-colors hover:bg-night-700/60 hover:text-night-50"
      >
        <IconLogIn className="h-5 w-5" />
      </Link>
    );
  }

  const email = session.data.user.email ?? '';
  const initial = email.charAt(0).toUpperCase();

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/', { replace: true });
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div className="flex items-center gap-1">
      <Link
        to="/settings"
        aria-label={`Settings — signed in as ${email}`}
        title={email}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-night-700 text-sm font-semibold text-night-100 transition-colors hover:bg-night-600"
      >
        {initial || '·'}
      </Link>
      <button
        type="button"
        onClick={handleSignOut}
        disabled={signingOut}
        aria-label={signingOut ? 'Signing out' : 'Sign out'}
        title="Sign out"
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-night-200 transition-colors hover:bg-night-700/60 hover:text-night-50 disabled:opacity-50"
      >
        <IconLogOut className="h-5 w-5" />
      </button>
    </div>
  );
}
